/** useMediaRecorder — microphone recording via the MediaRecorder API.
 *
 *  Pure frontend: captures audio from ``getUserMedia``, accumulates chunks
 *  via ``MediaRecorder``, and exposes the resulting Blob to the caller on
 *  stop. Supports pause/resume and reports elapsed seconds while recording.
 *
 *  Codec selection prefers ``audio/webm;codecs=opus`` (Chrome/Firefox) then
 *  ``audio/mp4`` (Safari) so the produced blob plays back in the recorder-
 *  pen player without transcoding. The chosen MIME is exposed via
 *  ``mime`` so callers can derive the file extension when uploading.
 *
 *  Cleanup: every MediaStream track is stopped on unmount or when ``reset``
 *  is called after a completed capture, so the browser mic indicator clears.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type MediaRecorderState = "idle" | "recording" | "paused" | "error";

export interface UseMediaRecorderResult {
  state: MediaRecorderState;
  /** Elapsed seconds while recording or paused. Resets on stop. */
  durationSeconds: number;
  /** Populated after stop(). Null while recording or after reset(). */
  blob: Blob | null;
  /** MIME of the captured blob — drives the file extension at upload. */
  mime: string | null;
  error: string | null;
  isSupported: boolean;
  start: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<Blob | null>;
  /** Discard the current capture and return to idle. Releases the mic. */
  reset: () => void;
}

/** MIME candidates in preference order. The first one ``isTypeSupported``
 *  wins. ``audio/webm`` is preferred because opus is small and crisp; mp4
 *  is the Safari fallback. */
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

export function isMediaRecorderSupported(): boolean {
  if (typeof window === "undefined") return false;
  if (!("MediaRecorder" in window)) return false;
  if (!navigator?.mediaDevices?.getUserMedia) return false;
  return true;
}

function pickSupportedMime(): string | null {
  if (typeof window === "undefined" || !("MediaRecorder" in window)) return null;
  for (const candidate of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {
      // Some browsers throw on isTypeSupported with weird input — keep going.
    }
  }
  return null;
}

/** Map a recording MIME to a file extension for upload. Falls back to .bin. */
export function mimeToExtension(mime: string | null): string {
  if (!mime) return ".bin";
  const lower = mime.toLowerCase();
  if (lower.includes("webm")) return ".webm";
  if (lower.includes("mp4") || lower.includes("aac")) return ".m4a";
  if (lower.includes("mpeg")) return ".mp3";
  if (lower.includes("wav")) return ".wav";
  if (lower.includes("ogg")) return ".ogg";
  if (lower.includes("flac")) return ".flac";
  return ".bin";
}

export function useMediaRecorder(): UseMediaRecorderResult {
  const [state, setState] = useState<MediaRecorderState>("idle");
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [mime, setMime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Resolve handle for the in-flight stop() promise. Set when stop is called,
  // cleared after onstop handler resolves.
  const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null);

  const isSupported = isMediaRecorderSupported();

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      for (const track of stream.getTracks()) {
        try {
          track.stop();
        } catch {
          // Track already stopped — ignore.
        }
      }
    }
    streamRef.current = null;
  }, []);

  const reset = useCallback(() => {
    stopTimer();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {
        // Defensive: an already-stopped recorder throws.
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    mimeRef.current = null;
    setMime(null);
    setBlob(null);
    setDurationSeconds(0);
    setError(null);
    setState("idle");
    releaseStream();
  }, [releaseStream, stopTimer]);

  const start = useCallback(async () => {
    if (!isSupported) {
      setError("当前浏览器不支持录音（MediaRecorder API 缺失）");
      setState("error");
      return;
    }
    if (state === "recording" || state === "paused") return;

    const chosenMime = pickSupportedMime();
    if (!chosenMime) {
      setError("当前浏览器不支持任何可用的音频编码");
      setState("error");
      return;
    }

    // Reset capture state — a previous blob should not survive a new run.
    chunksRef.current = [];
    setBlob(null);
    setError(null);
    setDurationSeconds(0);
    mimeRef.current = chosenMime;
    setMime(chosenMime);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const e = err as DOMException;
      const msg =
        e?.name === "NotAllowedError"
          ? "麦克风权限被拒绝，请在浏览器设置中允许后重试"
          : e?.name === "NotFoundError"
            ? "未检测到麦克风设备"
            : `无法访问麦克风：${e?.message ?? "未知错误"}`;
      setError(msg);
      setState("error");
      return;
    }
    streamRef.current = stream;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: chosenMime });
    } catch (err) {
      const e = err as DOMException;
      setError(`初始化录音失败：${e?.message ?? "未知错误"}`);
      setState("error");
      releaseStream();
      return;
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onerror = (event: Event) => {
      const e = event as unknown as { error?: DOMException };
      const msg = e?.error?.message ?? "录音过程中发生错误";
      setError(msg);
      setState("error");
      stopTimer();
    };

    recorder.onstop = () => {
      const capturedMime = mimeRef.current ?? chosenMime;
      const assembled = new Blob(chunksRef.current, { type: capturedMime });
      chunksRef.current = [];
      setBlob(assembled);
      setMime(capturedMime);
      setState("idle");
      stopTimer();
      releaseStream();
      // Resolve the in-flight stop() promise if any.
      const resolve = stopResolveRef.current;
      stopResolveRef.current = null;
      if (resolve) resolve(assembled);
    };

    // Use 1-second timeslices so ondataavailable fires periodically — some
    // browsers won't deliver anything until stop if timeslice is omitted,
    // which means a crash mid-record loses everything.
    recorder.start(1000);
    setState("recording");
    timerRef.current = setInterval(() => {
      setDurationSeconds((s) => s + 1);
    }, 1000);
  }, [isSupported, state, releaseStream, stopTimer]);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    try {
      recorder.pause();
      setState("paused");
      stopTimer();
    } catch {
      // Ignore — pause failure is non-fatal.
    }
  }, [stopTimer]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    try {
      recorder.resume();
      setState("recording");
      // Resume the visible timer from where it left off.
      timerRef.current = setInterval(() => {
        setDurationSeconds((s) => s + 1);
      }, 1000);
    } catch {
      // Ignore — resume failure is non-fatal.
    }
  }, []);

  const stop = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      // Nothing to stop — return whatever blob we already have.
      return blob;
    }
    return new Promise<Blob | null>((resolve) => {
      stopResolveRef.current = resolve;
      try {
        recorder.stop();
      } catch (err) {
        // If stop throws, resolve with whatever we have and surface the error.
        stopResolveRef.current = null;
        setError(`停止录音失败：${(err as DOMException)?.message ?? "未知错误"}`);
        setState("error");
        resolve(null);
      }
    });
  }, [blob]);

  // Cleanup on unmount: stop the recorder, timer, and release the mic.
  useEffect(() => {
    return () => {
      stopTimer();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // Already stopped — ignore.
        }
      }
      releaseStream();
    };
  }, [releaseStream, stopTimer]);

  return {
    state,
    durationSeconds,
    blob,
    mime,
    error,
    isSupported,
    start,
    pause,
    resume,
    stop,
    reset,
  };
}
